import { type MutableRefObject, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { CapturePhase } from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import type { SimpleLandmark } from "./faceCrop";
import { useSharedFaceLandmarker } from "./FaceLandmarkerProvider";
import { MEDIAPIPE_STUCK_TIMEOUT_MS } from "./mediapipeConfig";
const PROFILE_YAW_THRESHOLD = 10 as const;
const PROFILE_STABLE_FRAMES = 3 as const;

/**
 * `onStuckTimeoutFired` is an optional observability hook. The hook itself
 * does not log — it stays free of assessment/i18n concerns so it can be
 * reused outside the assessment flow. Consumers that run inside an
 * assessment should wire this to `assessmentLogger.warn(...)` so Datadog
 * gets full `{ assessmentId, subdomainSlug, flowType, phase }` correlation
 * for the specific session. The callback is invoked at most once per
 * profile phase entry (cleared on phase-exit in the reset effect).
 */
type UseProfileAlignmentOptions = {
  phase: CapturePhase;
  videoRef: RefObject<HTMLVideoElement | null>;
  onStuckTimeoutFired?: (phase: CapturePhase) => void;
};
type UseProfileAlignmentReturn = {
  isGuidanceAvailable: boolean;
  leftProfileReady: boolean;
  rightProfileReady: boolean;
  /**
   * True once we have waited `MEDIAPIPE_STUCK_TIMEOUT_MS` without detecting a
   * stable yaw above threshold (or ever seeing a single face) in the current
   * profile phase. Consumers should use this to enable manual shutter
   * capture even though guidance hasn't confirmed alignment — this is the
   * escape hatch that prevents users from being stuck forever in poor
   * lighting, tight frames, or when they can't physically turn their head
   * far enough to trip the yaw threshold.
   */
  isAlignmentStuck: boolean;
  getDetectedProfileSide: () => "left" | "right" | null;
  lastLandmarksRef: MutableRefObject<SimpleLandmark[] | null>;
};
export function useProfileAlignment({
  phase,
  videoRef,
  onStuckTimeoutFired
}: UseProfileAlignmentOptions): UseProfileAlignmentReturn {
  const onStuckTimeoutFiredRef = useRef(onStuckTimeoutFired);
  onStuckTimeoutFiredRef.current = onStuckTimeoutFired;
  const {
    isInitialized: isProfileDetectorReady,
    runtimeError: profileDetectorRuntimeError,
    startDetection,
    stopDetection
  } = useSharedFaceLandmarker();

  // If the shared landmarker has entered a runtime-error state (procrustes
  // RET_CHECK on this device, reinit failed), we cannot offer yaw-based
  // profile guidance. Fall back to unguided manual capture by reporting
  // guidance as unavailable — ProfileCaptureStage already allows the
  // shutter when isGuidanceAvailable is false.
  const isGuidanceAvailable = isProfileDetectorReady && !profileDetectorRuntimeError;
  const [leftProfileReady, setLeftProfileReady] = useState(false);
  const [rightProfileReady, setRightProfileReady] = useState(false);
  const [isAlignmentStuck, setIsAlignmentStuck] = useState(false);
  const leftReadyRef = useRef(false);
  const rightReadyRef = useRef(false);
  const leftStableRef = useRef(0);
  const rightStableRef = useRef(0);
  const detectedSideRef = useRef<"left" | "right" | null>(null);
  const lastLandmarksRef = useRef<SimpleLandmark[] | null>(null);
  const hasFiredStuckTimeoutRef = useRef(false);
  const isLeftPhase = phase === "profile-left-direction" || phase === "profile-left-countdown";
  const isRightPhase = phase === "profile-right-direction" || phase === "profile-right-countdown";
  const isProfilePhase = isLeftPhase || isRightPhase;
  const updateLeftReady = useCallback((value: boolean) => {
    if (leftReadyRef.current === value) return;
    leftReadyRef.current = value;
    setLeftProfileReady(value);
  }, []);
  const updateRightReady = useCallback((value_0: boolean) => {
    if (rightReadyRef.current === value_0) return;
    rightReadyRef.current = value_0;
    setRightProfileReady(value_0);
  }, []);
  useEffect(() => {
    if (isProfilePhase) return;
    leftStableRef.current = 0;
    rightStableRef.current = 0;
    leftReadyRef.current = false;
    rightReadyRef.current = false;
    detectedSideRef.current = null;
    lastLandmarksRef.current = null;
    hasFiredStuckTimeoutRef.current = false;
    if (leftProfileReady) {
      queueMicrotask(() => setLeftProfileReady(false));
    }
    if (rightProfileReady) {
      queueMicrotask(() => setRightProfileReady(false));
    }
    if (isAlignmentStuck) {
      queueMicrotask(() => setIsAlignmentStuck(false));
    }
  }, [isProfilePhase, leftProfileReady, rightProfileReady, isAlignmentStuck]);

  // Stuck-timeout escape hatch. Shares `MEDIAPIPE_STUCK_TIMEOUT_MS` with
  // useFaceGuidance: after this long with guidance available but no alignment
  // achieved, we flip `isAlignmentStuck` so ProfileCaptureStage can enable
  // the shutter button and let the user capture manually. This is the *only*
  // recovery path when MediaPipe initialized fine but the user physically
  // can't trip the yaw threshold (bad framing, cropped video, limited neck
  // mobility, very low light for landmark detection).
  //
  // If the user *does* achieve alignment at any point during the phase, we
  // deliberately keep `isAlignmentStuck` at false — we don't need to lock in
  // the escape hatch mid-phase, and resetting here would defeat its purpose
  // if they drift out of alignment again.
  useEffect(() => {
    if (!isProfilePhase || !isGuidanceAvailable) return;
    // Once we've already fallen into the stuck state for this phase, don't
    // re-arm the timer — the phase-exit effect above clears everything.
    if (isAlignmentStuck) return;
    if (leftProfileReady || rightProfileReady) return;
    const timeout = setTimeout(() => {
      setIsAlignmentStuck(true);
      if (!hasFiredStuckTimeoutRef.current) {
        hasFiredStuckTimeoutRef.current = true;
        onStuckTimeoutFiredRef.current?.(phase);
      }
    }, MEDIAPIPE_STUCK_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isProfilePhase, isGuidanceAvailable, isAlignmentStuck, leftProfileReady, rightProfileReady, phase]);
  useEffect(() => {
    if (!isGuidanceAvailable || !videoRef.current || !isProfilePhase) {
      stopDetection();
      return;
    }
    startDetection(videoRef.current, result => {
      if (result.faceCount !== 1) {
        leftStableRef.current = 0;
        rightStableRef.current = 0;
        lastLandmarksRef.current = null;
        updateLeftReady(false);
        updateRightReady(false);
        return;
      }
      if (result.landmarks && result.landmarks.length > 0) {
        lastLandmarksRef.current = result.landmarks[0];
      }
      const isLeftAligned = result.yaw >= PROFILE_YAW_THRESHOLD;
      leftStableRef.current = isLeftAligned ? leftStableRef.current + 1 : 0;
      const isLeftStable = leftStableRef.current >= PROFILE_STABLE_FRAMES;
      updateLeftReady(isLeftStable);
      const isRightAligned = result.yaw <= -PROFILE_YAW_THRESHOLD;
      rightStableRef.current = isRightAligned ? rightStableRef.current + 1 : 0;
      const isRightStable = rightStableRef.current >= PROFILE_STABLE_FRAMES;
      updateRightReady(isRightStable);
      if (isLeftStable) {
        detectedSideRef.current = "left";
      } else if (isRightStable) {
        detectedSideRef.current = "right";
      }
    });
    return () => {
      stopDetection();
    };
  }, [isProfilePhase, isGuidanceAvailable, startDetection, stopDetection, updateLeftReady, updateRightReady, videoRef]);
  const getDetectedProfileSide = useCallback(() => detectedSideRef.current, []);
  return {
    isGuidanceAvailable,
    leftProfileReady,
    rightProfileReady,
    isAlignmentStuck,
    getDetectedProfileSide,
    lastLandmarksRef
  };
}